import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import SEOHead from "@/components/SEOHead";
import { Link } from "@/lib/router-compat";
import {
  Printer,
  FileDown,
  Phone,
  ArrowLeft,
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  FileText,
} from "lucide-react";
import {
  RelatedPages,
  allTreatmentsLinks,
  allServiceLinks,
  allDecisionLinks,
} from "@/components/RelatedPages";

const PatientEligibilityChecklist = () => {
  const sections = [
    {
      title: "Patient Demographics & Contact",
      items: [
        "Patient full legal name and date of birth (must be 22 or older for FDA-cleared SAINT®; 18+ for standard TMS / SPRAVATO®)",
        "Primary phone number, mobile number, and email address",
        "Current residential address",
        "Preferred language (English / Spanish — bilingual clinical staff available)",
      ],
    },
    {
      title: "Diagnosis & Treatment-Resistant Depression History",
      items: [
        "Primary psychiatric diagnosis with ICD-10 code (e.g., F32.1, F32.2, F33.1, F33.2 Major Depressive Disorder)",
        "Patient is currently experiencing a major depressive episode",
        "List of prior antidepressant trials (minimum 2 adequate trials of differing classes at therapeutic dose for ≥6–8 weeks)",
        "Trial details: medication name, maximum dose, duration, response, and reason discontinued (inefficacy vs. intolerable side effects)",
        "Prior psychotherapy trials (CBT, psychodynamic, etc.) and clinical response",
        "History of prior neuromodulation (standard TMS, ECT, VNS) or ketamine/SPRAVATO® therapy with dates and response",
      ],
    },
    {
      title: "Current Clinical Status & Comorbidities",
      items: [
        "Current psychiatric medications, doses, and prescribing clinician",
        "Comorbid conditions documented (generalized anxiety, OCD, PTSD, panic disorder)",
        "Absence of active bipolar I disorder or current/recent psychotic symptoms (unless co-managed and clinically cleared)",
        "Substance use history: active severe substance use disorder must be stabilized prior to intensive outpatient protocol",
        "Baseline depression severity rating (PHQ-9, MADRS, or HAM-D score within past 30 days)",
      ],
    },
    {
      title: "Critical Safety Screening & Contraindications",
      items: [
        "NO non-removable ferromagnetic metal in or within 30 cm of the head (aneurysm clips, cochlear implants, deep brain stimulators, metal plates, ocular metallic fragments)",
        "NO implanted medical devices controlled by physiological signals (cardiac pacemakers, ICDs, vagus nerve stimulators, medication pumps)",
        "NO history of unprovoked seizures, epilepsy, or known increased intracranial pressure",
        "Patient is NOT currently pregnant (unless cleared by treating obstetrician and psychiatrist after risk-benefit discussion)",
        "Patient is eligible and safe for 3T MRI scanning (required for individualized fMRI-guided target selection on the Magnus System)",
      ],
    },
    {
      title: "Protocol Commitment & Logistics",
      items: [
        "SAINT® protocol commitment: patient able to attend 5 consecutive business days (Monday–Friday, ~9:00 AM – 5:30 PM, 10 sessions/day with 50-minute rest intervals)",
        "Standard TMS alternative: patient able to attend 1 session/day, 5 days/week for 6–9 weeks (~36 sessions total)",
        "Transportation: patient arranged daily transport to/from clinic (no sedation is used; patients can drive themselves, but travel companions are welcomed)",
        "For out-of-town patients: confirmed travel plan to Coral Springs, FL clinic with accommodations arranged",
      ],
    },
    {
      title: "Referring Clinician & Co-Management Information",
      items: [
        "Referring provider full name, title, credentials, and specialty",
        "Practice name, clinic address, direct phone, and secure HIPAA fax number",
        "National Provider Identifier (NPI) number and state medical license number",
        "Preferred communication method for progress reports and post-treatment clinical handback summary",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Patient Eligibility Checklist for Physician Referrals | Neuro Wellness TMS"
        description="Clinically approved referral checklist and co-management protocol for physicians referring patients for SAINT® accelerated TMS, standard TMS, and SPRAVATO® in Coral Springs, FL."
        canonical="/patient-eligibility-checklist"
        keywords="TMS patient eligibility checklist, physician referral form, SAINT TMS referral, depression treatment referral Coral Springs, co-management psychiatric protocol"
      />

      <Navbar />
      <main className="pt-16 sm:pt-20">
        {/* Hero / Banner */}
        <section className="bg-gradient-navy text-primary-foreground py-12 sm:py-16 print:py-4 print:bg-white print:text-black">
          <div className="container-px mx-auto max-w-4xl">
            <Link
              to="/learn/refer-a-patient-for-saint"
              className="inline-flex items-center gap-1.5 text-sm text-primary-foreground/70 hover:text-accent transition-colors mb-4 print:hidden"
            >
              <ArrowLeft className="h-4 w-4" />
              View Clinician Referral & Co-Management Guide
            </Link>
            <div className="flex items-center gap-2 mb-2 print:hidden">
              <span className="rounded-full bg-accent/20 px-3 py-1 text-xs font-semibold text-accent uppercase tracking-wide">
                Clinician Resource · One-Page Referral Form
              </span>
            </div>
            <h1 className="font-headings text-3xl sm:text-4xl font-bold mb-3">
              Physician Referral & Patient Eligibility Checklist
            </h1>
            <p className="text-lg text-primary-foreground/80 max-w-3xl leading-relaxed print:text-black print:text-sm">
              Use this clinically approved checklist to identify appropriate
              candidates for SAINT® accelerated TMS (50 sessions over 5 days,
              fMRI-guided), conventional daily TMS (6–9 weeks), and SPRAVATO®
              (esketamine). Final clinical eligibility is determined during
              medical consultation by our medical director, Daniel Pietras, MD.
            </p>
            <div className="mt-6 flex flex-wrap gap-3 print:hidden">
              <button
                onClick={() => window.print()}
                className="inline-flex items-center gap-2 rounded-lg bg-accent px-5 py-2.5 text-sm font-semibold text-white shadow-md hover:bg-accent/90 transition-colors"
              >
                <FileDown className="h-4 w-4" />
                Download / Print One-Page Form
              </button>
              <button
                onClick={() => window.print()}
                className="inline-flex items-center gap-2 rounded-lg border-2 border-white/40 bg-transparent px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
              >
                <Printer className="h-4 w-4" />
                Print Checklist
              </button>
              <Link
                to="/learn/refer-a-patient-for-saint"
                className="inline-flex items-center gap-2 rounded-lg bg-white/10 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/20 transition-colors"
              >
                <FileText className="h-4 w-4" />
                Read Co-Management Protocol
              </Link>
            </div>
          </div>
        </section>

        {/* Clinical Pathway Overview */}
        <section className="container-px mx-auto max-w-4xl pt-8 pb-4 print:pt-2 print:pb-2">
          <div className="grid gap-4 sm:grid-cols-4 text-center">
            <div className="rounded-xl border border-border bg-card p-4 shadow-sm print:p-2">
              <div className="text-xs font-bold uppercase tracking-wider text-accent mb-1">
                Step 1
              </div>
              <div className="font-semibold text-primary text-sm">
                Clinical Intake
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Review referral records & preliminary telehealth/phone screening
              </p>
            </div>
            <div className="rounded-xl border border-border bg-card p-4 shadow-sm print:p-2">
              <div className="text-xs font-bold uppercase tracking-wider text-accent mb-1">
                Step 2
              </div>
              <div className="font-semibold text-primary text-sm">
                fMRI Brain Mapping
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Resting-state functional MRI to calculate individual sgACC-dlPFC
                target
              </p>
            </div>
            <div className="rounded-xl border border-border bg-card p-4 shadow-sm print:p-2">
              <div className="text-xs font-bold uppercase tracking-wider text-accent mb-1">
                Step 3
              </div>
              <div className="font-semibold text-primary text-sm">
                5 Treatment Days
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                10 daily iTBS sessions on Magnus System (50 total) Monday–Friday
              </p>
            </div>
            <div className="rounded-xl border border-border bg-card p-4 shadow-sm print:p-2">
              <div className="text-xs font-bold uppercase tracking-wider text-accent mb-1">
                Step 4
              </div>
              <div className="font-semibold text-primary text-sm">
                Clinical Handback
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Full completion report & return to referring provider for care
              </p>
            </div>
          </div>
        </section>

        {/* Checklist Content */}
        <section className="container-px mx-auto max-w-4xl py-6 sm:py-10 print:py-2">
          <div className="bg-card rounded-2xl border border-border p-6 sm:p-10 shadow-soft print:shadow-none print:border-0 print:rounded-none print:p-0">
            {/* Print-only header */}
            <div className="hidden print:block mb-4 pb-2 border-b-2 border-primary">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold text-primary">
                    Neuro Wellness TMS Centers of America
                  </h2>
                  <p className="text-xs text-foreground/70">
                    Medical Director: Daniel Pietras, MD · Verified SAINT®
                    Provider
                  </p>
                  <p className="text-xs text-foreground/70">
                    9734 West Sample Road, 2nd Floor - Suite B, Coral Springs,
                    FL 33065
                  </p>
                </div>
                <div className="text-right text-xs text-foreground/80">
                  <p>
                    <strong>Phone:</strong> 954-827-9793
                  </p>
                  <p>
                    <strong>Secure Fax:</strong> 954-827-9793
                  </p>
                  <p>
                    <strong>Web:</strong> neurowellnesstms.com
                  </p>
                </div>
              </div>
              <h3 className="text-sm font-bold uppercase tracking-wide mt-2 pt-2 border-t border-border">
                Physician Referral & Patient Eligibility Checklist (SAINT® /
                Standard TMS / SPRAVATO®)
              </h3>
            </div>

            {/* Intro notice */}
            <div className="mb-6 rounded-lg bg-muted/40 p-4 border border-border print:p-2 print:mb-3">
              <p className="text-sm text-foreground/85 leading-relaxed print:text-xs">
                <strong>Clinician Instructions:</strong> Complete this checklist
                to expedite your patient&apos;s evaluation. Fax completed form
                along with recent psychiatric notes, medication history, and
                current labs (if available) to our confidential intake fax at{" "}
                <strong>954-827-9793</strong>. We maintain strict non-retention
                of patients: our role is specialized neuromodulation delivery;
                long-term medication and psychotherapy management remains with
                your practice.
              </p>
            </div>

            {/* Checklist Sections */}
            <div className="space-y-6 print:space-y-3">
              {sections.map((section, sIdx) => (
                <div key={sIdx} className="break-inside-avoid">
                  <h3 className="font-headings text-base font-bold text-primary mb-2 pb-1 border-b border-border flex items-center justify-between">
                    <span>
                      {sIdx + 1}. {section.title}
                    </span>
                    <span className="text-xs font-normal text-muted-foreground print:hidden">
                      Check all that apply
                    </span>
                  </h3>
                  <ul className="space-y-1.5 print:space-y-1">
                    {section.items.map((item, iIdx) => (
                      <li
                        key={iIdx}
                        className="flex items-start gap-2.5 text-sm text-foreground/85 leading-relaxed print:text-xs"
                      >
                        <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border border-primary/40 text-primary/60">
                          <span className="text-[10px]">☐</span>
                        </span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Clinical Notes */}
            <div className="mt-6 print:mt-3 break-inside-avoid">
              <h3 className="font-headings text-base font-bold text-primary mb-2 pb-1 border-b border-border">
                7. Relevant Clinical History & Specific Questions for Neuro
                Wellness
              </h3>
              <div className="h-24 rounded-lg border-2 border-dashed border-border bg-muted/20 p-2 text-xs text-muted-foreground print:bg-white print:h-20" />
            </div>

            {/* Co-Management & Handback Commitment */}
            <div className="mt-6 rounded-lg bg-primary/5 border border-primary/20 p-4 text-xs text-foreground/80 leading-relaxed print:mt-2 print:p-2">
              <strong className="text-primary font-semibold block mb-1">
                Our Co-Management & Handback Commitment to Referring Providers:
              </strong>
              Neuro Wellness TMS Centers of America provides interventional
              treatment only. Within 5 business days of treatment completion,
              our clinical team faxes a formal handback summary to your office
              detailing: (1) baseline and post-treatment psychometric scores
              (PHQ-9 / MADRS), (2) exact treatment parameters delivered (50
              sessions, fMRI coordinates, motor threshold), (3) tolerability and
              side-effect summary, and (4) clinical recommendations for
              maintenance. Your patient returns directly to your care for
              ongoing pharmacotherapy and psychotherapy.
            </div>

            {/* Signature block */}
            <div className="mt-6 print:mt-3 grid sm:grid-cols-2 gap-6 print:gap-3 break-inside-avoid">
              <div>
                <p className="text-xs font-semibold text-foreground/70 mb-4">
                  Referring Provider Attestation & Signature
                </p>
                <div className="border-b border-foreground/40 h-6" />
                <p className="text-[10px] text-foreground/50 mt-1">
                  Provider Signature & Credentials (MD / DO / NP / PA)
                </p>
                <div className="border-b border-foreground/40 h-6 mt-3" />
                <p className="text-[10px] text-foreground/50 mt-1">
                  Date & Direct Contact Phone
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold text-foreground/70 mb-4">
                  Neuro Wellness Intake Review (Internal Use Only)
                </p>
                <div className="border-b border-foreground/40 h-6" />
                <p className="text-[10px] text-foreground/50 mt-1">
                  Received & Reviewed By
                </p>
                <div className="border-b border-foreground/40 h-6 mt-3" />
                <p className="text-[10px] text-foreground/50 mt-1">
                  Physician Consultation Scheduled Date
                </p>
              </div>
            </div>

            {/* Submission Instructions */}
            <div className="mt-6 print:mt-3 rounded-lg bg-light-blue/40 border border-light-blue p-4">
              <h4 className="font-headings text-sm font-bold text-primary mb-1">
                How to Submit This Referral Securely
              </h4>
              <p className="text-xs text-foreground/80 leading-relaxed mb-2">
                To protect protected health information (PHI), please do not
                submit patient clinical history through public web forms. Use
                our secure HIPAA clinical channels:
              </p>
              <div className="grid sm:grid-cols-2 gap-2 text-xs text-foreground/80">
                <div>
                  <strong className="text-primary">
                    Confidential Clinical Fax:
                  </strong>{" "}
                  954-827-9793
                </div>
                <div>
                  <strong className="text-primary">
                    Physician-to-Physician Line:
                  </strong>{" "}
                  954-827-9793
                </div>
                <div>
                  <strong className="text-primary">Facility:</strong> 9734 W
                  Sample Rd, Ste B, Coral Springs, FL 33065
                </div>
                <div>
                  <strong className="text-primary">Response Time:</strong>{" "}
                  Referring office contacted within 1 business day
                </div>
              </div>
            </div>

            {/* Crisis line */}
            <div className="mt-4 rounded-lg bg-primary/5 border border-primary/15 px-3 py-2 print:mt-2">
              <p className="text-xs text-foreground/80">
                <strong>Emergency / Acute Crisis Notice:</strong> If your
                patient is experiencing acute suicidal ideation with intent or
                psychiatric emergency, direct them to the nearest emergency
                department or call/text{" "}
                <a
                  href="tel:988"
                  className="font-bold text-accent underline decoration-accent/50 underline-offset-2"
                >
                  988
                </a>{" "}
                (Suicide &amp; Crisis Lifeline, 24/7). Neuro Wellness is an
                outpatient facility and does not provide emergency psychiatric
                crisis stabilization.
              </p>
            </div>
          </div>

          {/* Bottom Actions */}
          <div className="mt-6 print:hidden flex flex-wrap justify-between items-center gap-4 bg-muted/30 p-4 rounded-xl border border-border">
            <div>
              <p className="font-semibold text-primary text-sm">
                Need to discuss a complex case before referring?
              </p>
              <p className="text-xs text-muted-foreground">
                Our clinical team welcomes peer-to-peer discussions regarding
                candidacy and fMRI targeting.
              </p>
            </div>
            <div className="flex gap-2">
              <a
                href="tel:9548279793"
                className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-accent/90 transition-colors"
              >
                <Phone className="h-3.5 w-3.5" />
                Call 954-827-9793
              </a>
              <button
                onClick={() => window.print()}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-4 py-2 text-xs font-semibold text-primary hover:bg-muted transition-colors"
              >
                <Printer className="h-3.5 w-3.5" />
                Print Referral Sheet
              </button>
            </div>
          </div>
        </section>

        <RelatedPages
          heading="Clinician & Referral Resources"
          links={[
            {
              title: "Clinician Guide: Refer a Patient for SAINT®",
              href: "/learn/refer-a-patient-for-saint",
              description:
                "Detailed referral workflow, co-management protocol, and fMRI targeting requirements.",
            },
            {
              title: "What Is a Verified SAINT® Provider?",
              href: "/learn/what-is-a-verified-saint-provider",
              description:
                "Five verification checks confirming genuine Magnus Medical technology.",
            },
            {
              title: "SAINT® Protocol: 50 Sessions in 5 Days",
              href: "/learn/saint-tms-protocol",
              description:
                "Full clinical parameters, dosing, and session schedules.",
            },
            {
              title: "SAINT® TMS Cost & Insurance Guide",
              href: "/learn/saint-tms-cost",
              description:
                "Self-pay rates, insurance prior authorization, and benefits verification.",
            },
            ...allTreatmentsLinks.slice(0, 2),
          ]}
        />
      </main>
      <Footer />
    </div>
  );
};

export default PatientEligibilityChecklist;
